import React from 'react';
import type { FilterSelection } from '@prerender/design-system';

import type { NoCrawlersSelection } from '~features/crawler-filter/CrawlerFilterHooks';
import { type AiCrawlerLineup, useAiCrawlerLineup } from '../../shared';
import useQueryStringParameters from '../../../../utils/useQueryStringParameters';
import { CATEGORY_TO_CRAWLER_IDS, CRAWLER_ID_TO_CATEGORY } from '../DiscoverabilityCrawlers';
import {
  buildDiscoverabilityQueryParameters,
  CLEARED_SCAN_SCOPE,
  QueryParamNames,
  type DiscoverabilityQueryParams,
} from '../DiscoverabilityQueryParameters';
import useTrackDiscoverabilityEvent from './useTrackDiscoverabilityEvent';

const ALL_CRAWLERS_LABEL = 'All Crawlers';
const NO_CRAWLERS_LABEL = 'No Crawlers';

function providerCategoriesOf(crawlerIds: readonly string[], lineup: AiCrawlerLineup): string[] {
  return Array.from(
    new Set(
      crawlerIds
        .map((id) => CRAWLER_ID_TO_CATEGORY[id])
        .filter((category) => lineup.categories.includes(category))
    )
  );
}

function formatCrawlerValue(lineup: AiCrawlerLineup, selection: FilterSelection): string {
  if (selection === null) return NO_CRAWLERS_LABEL;
  const isAllCrawlers = selection.length === 0 || selection.length === lineup.categories.length;
  return isAllCrawlers ? ALL_CRAWLERS_LABEL : selection.join(', ');
}

export type DiscoverabilityCrawlerFilter = {
  lineup: AiCrawlerLineup;
  pickerCategories: string[];
  pickerSelection: FilterSelection;
  pickCrawlers: (selection: FilterSelection) => void;
};

export function useDiscoverabilityCrawlerFilter(crawlerSelection: NoCrawlersSelection): DiscoverabilityCrawlerFilter {
  const { noCrawlersSelected, setNoCrawlersSelected, noteOwnNavigation } = crawlerSelection;
  const lineup = useAiCrawlerLineup();
  const trackEvent = useTrackDiscoverabilityEvent();
  const { queryStrings, updateMultipleParameters } =
    useQueryStringParameters<DiscoverabilityQueryParams>(buildDiscoverabilityQueryParameters);
  const selectedCrawlerIds = queryStrings[QueryParamNames.Crawlers];

  const pickerCategories = React.useMemo(() => [...lineup.categories], [lineup]);
  const pickerSelection = React.useMemo<FilterSelection>(() => {
    if (noCrawlersSelected) return null;
    if (!selectedCrawlerIds) return [];
    const categories = providerCategoriesOf(selectedCrawlerIds, lineup);
    return categories.length === lineup.categories.length ? [] : categories;
  }, [noCrawlersSelected, selectedCrawlerIds, lineup]);

  const pickCrawlers = (selection: FilterSelection) => {
    trackEvent('Discoverability: Filter Changed', {
      filter_name: 'crawler',
      old_value: formatCrawlerValue(lineup, pickerSelection),
      new_value: formatCrawlerValue(lineup, selection),
    });
    setNoCrawlersSelected(selection === null);
    if (selection === null) return;

    const allCrawlersSelected = selection.length === 0 || selection.length === lineup.categories.length;
    const crawlerIds = selection.flatMap((category) => CATEGORY_TO_CRAWLER_IDS[category] ?? []);
    noteOwnNavigation();
    updateMultipleParameters({
      crawlers: allCrawlersSelected ? undefined : crawlerIds,
      ...CLEARED_SCAN_SCOPE,
    });
  };

  return { lineup, pickerCategories, pickerSelection, pickCrawlers };
}
